import React, { useEffect, useMemo, useState } from "react";
import { GenericDialog } from "../../../../components/generic/GenericDialog";
import { Badge, Box, Button, Card, Grid2 as Grid, IconButton, Stack, Typography } from "@mui/material";
import { GenericAvatar } from "../../../../components/generic/GenericAvatar";
import { getUserDetails } from "../../../../config/apisauce";
import { formatDate } from "../../../../helpers/dateHelper";
import {
  COLLAB_INTEREST,
  COLLAB_INTEREST_NSFW,
  COLLAB_INTEREST_SFW,
  CONTENT_INTEREST,
  CONTENT_INTEREST_NSFW,
  CONTENT_INTEREST_SFW,
  DECLINE_CHECKLIST,
  PLATFORMS_KEY,
  SOCIALS_KEY
} from "../../../../enums/generalEnums";
import { ProfileInterests } from "./ProfileInterests";
import { useTheme } from "@emotion/react";
import { filterArray } from "../../../../helpers/arrayHelper";
import { stripBaseUrl } from "../../../../helpers/stringHelper";
import { LinksCard } from "./LinksCard";
import { DECLINE, EDIT } from "../../../../enums/statusEnums";
import { EditHeader } from "../../../creator/editProfilePage/EditHeader";
import { EditPlatformsForm } from "../../../creator/editProfilePage/EditPlatformsForm";
import { EditAboutMe } from "../../../creator/editProfilePage/EditAboutMe";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { SHADOWS } from "../../../../enums/themeEnum";
import { EDIT_PROFILE_FORMS } from "../../../../enums/editProfileEnums";
import { BASIC_INFO_KEY, BIO_KEY, PROFILE_PHOTO_KEY } from "../../../../enums/profileSetupEnums";
import { EditSocialsForm } from "../../../creator/editProfilePage/EditSocialsForm";
import { EditCollabInterestForm } from "../../../creator/editProfilePage/EditCollabInterestForm";
import { EditContentInterestForm } from "../../../creator/editProfilePage/EditContentInterestForm";
import { EditProfilePhotoForm } from "../../../creator/editProfilePage/EditProfilePhotoForm";
import { EditContactForm } from "../../../creator/editProfilePage/EditContactForm";
import { EditBasicInfoForm } from "../../../creator/editProfilePage/EditBasicInfoForm";

export const ProfileDetails = (props) => {
  const {
    openDialog,
    handleClose,
    userId,
    handleOpenReasonDialog,
    handleAcceptUser,
    isActionsHidden = false,
    refetchCreatorList,
    isBypassOndato
  } = props;

  const theme = useTheme();
  const titleProps = { variant: "h6", color: "primary", mb: 1.5, fontWeight: 700 };
  const buttonProps = { sx: { width: { xs: '100%', sm: '250px' } } };
  const [userDetails, setUserDetails] = useState({});
  const contactBaseUrl = stripBaseUrl(userDetails?.contact_details) ?? null;
  const [selectedEditFormDetails, setSelectedEditFormDetails] = useState(null);

  const handleEditSection = (index) => {
    setSelectedEditFormDetails(EDIT_PROFILE_FORMS[index]);
  };

  const fetchUserDetails = async () => {
    const { ok, data } = await getUserDetails({ user_id: userId });
    if (ok) {
      setUserDetails(data);
    }
  };

  const handleCancel = () => {
    setSelectedEditFormDetails(null);
  };

  const socialsAndPlatforms = useMemo(() => {
    return {
      platforms: filterArray(userDetails?.user?.category_values, PLATFORMS_KEY, 'category.type') ?? [],
      socials: filterArray(userDetails?.user?.category_values, SOCIALS_KEY, 'category.type') ?? []
    };
  }, [userDetails]);

  const genericFormProps = {
    creatorDetails: userDetails,
    handleClose: () => handleCancel(),
    refetchDetails: () => fetchUserDetails(),
  };

  const forms = {
    [PLATFORMS_KEY]: <EditPlatformsForm
      platformValues={socialsAndPlatforms?.platforms}
      {...genericFormProps}
    />,
    [SOCIALS_KEY]: <EditSocialsForm
      socialsValues={socialsAndPlatforms?.socials}
      {...genericFormProps}
    />,
    [BIO_KEY]: <EditAboutMe {...genericFormProps} />,
    [COLLAB_INTEREST]: <EditCollabInterestForm {...genericFormProps} />,
    [CONTENT_INTEREST]: <EditContentInterestForm {...genericFormProps} />,
    [PROFILE_PHOTO_KEY]: <EditProfilePhotoForm refetchCreatorList={() => refetchCreatorList()} {...genericFormProps} />,
    // [CONTACT_INFO_KEY]: <EditContactForm isPage={false} {...genericFormProps} />,
    [BASIC_INFO_KEY]: <EditBasicInfoForm refetchCreatorList={() => refetchCreatorList()}{...genericFormProps} />
  };

  useEffect(() => {
    if (openDialog && userId) fetchUserDetails();
  }, [openDialog, userId]);

  return (
    <GenericDialog
      openDialog={openDialog}
      handleClose={() => {
        handleClose();
        handleCancel();
      }}
      width={selectedEditFormDetails ? '854px' : '1039px'}
      title={selectedEditFormDetails ? `Edit ${selectedEditFormDetails?.label}` : 'Profile Details'}
    >
      {
        selectedEditFormDetails ? forms?.[selectedEditFormDetails?.key] : <Box sx={{ overflowX: 'auto', pr: 1 }}>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 5 }}>
                <Badge
                  badgeContent={<IconButton
                    onClick={() => handleEditSection(6)}
                    color="primary"
                    sx={{
                      backgroundColor: '#FFF',
                      boxShadow: SHADOWS.light,
                      ':hover': { backgroundColor: '#FFF' }
                    }}
                  >
                    <PencilSimpleLine size={16} weight="bold" />
                  </IconButton>
                  }
                  overlap="circular"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                >
                  <GenericAvatar size={'300px'} location={userDetails?.profile_picture?.location} />
                </Badge>
              </Box>
              <Card sx={{ minHeight: { xs: 'auto', sm: '784px' } }}>
                <EditHeader mb={1} label={'Platforms'} action={() => handleEditSection(0)} />
                <Stack gap={2}>
                  {
                    socialsAndPlatforms?.platforms?.map((platform, index) => {
                      return (
                        <LinksCard
                          icon={platform?.category?.name}
                          key={`platform-key-${index}`}
                          value={platform?.value}
                        />
                      );
                    })
                  }
                </Stack>
                <EditHeader label={'Socials'} sx={{ margin: '10px 0' }} action={() => handleEditSection(1)} />
                <Stack gap={2}>
                  {
                    socialsAndPlatforms?.socials?.map((social, index) => {
                      return (
                        <LinksCard
                          icon={social?.category?.name}
                          key={`social-key-${index}`}
                          value={social?.value}
                        />
                      );
                    })
                  }
                </Stack>
              </Card>
            </Grid>
            <Grid item size={{ xs: 12, sm: 7 }}>
              <Stack gap={2}>
                <Card>
                  <EditHeader mb={1} label={'Basic Information'} action={() => handleEditSection(5)} />
                  <Stack gap={0.5}>
                    <Typography variant="subtitle1"><b>Creator Name:</b> {userDetails?.user?.name}</Typography>
                    <Typography variant="subtitle1"><b>Email:</b> {userDetails?.user?.email}</Typography>
                    <Typography variant="subtitle1"><b>Birthday:</b> {formatDate(new Date(userDetails?.birthday))}</Typography>
                    <Typography variant="subtitle1"><b>Profession:</b> {userDetails?.profession}</Typography>
                    <Typography variant="subtitle1" textTransform={'capitalize'}><b>Gender:</b> {userDetails?.gender}</Typography>
                    <Typography variant="subtitle1"><b>Country:</b> {userDetails?.country}</Typography>
                    <Typography variant="subtitle1"><b>State/Region:</b> {userDetails?.state ?? "None"}</Typography>
                  </Stack>
                </Card>
                <Card sx={{ minHeight: { xs: 'auto', sm: '417px' } }}>
                  <EditHeader mb={1} label={'About Me'} action={() => handleEditSection(2)} />
                  <Typography variant="subtitle1">{userDetails?.bio}</Typography>
                </Card>
                <ProfileInterests
                  titleProps={titleProps}
                  title={<EditHeader mb={1} label={'Collab Interest'} action={() => handleEditSection(3)} />}
                  interests={userDetails?.user?.collab_interest}
                  sfwKey={COLLAB_INTEREST_SFW}
                  nsfwKey={COLLAB_INTEREST_NSFW}
                />
                <ProfileInterests
                  titleProps={titleProps}
                  title={<EditHeader mb={1} label={'Content Interest'} action={() => handleEditSection(4)} />}
                  interests={userDetails?.user?.content_interest}
                  sfwKey={CONTENT_INTEREST_SFW}
                  nsfwKey={CONTENT_INTEREST_NSFW}
                />
              </Stack>
            </Grid>
          </Grid>
          {/* <Card sx={{ mt: 3.5, minHeight: { xs: 'auto', sm: '368px' } }}>
            <EditHeader mb={1} label={'Collab Contact Method'} action={() => handleEditSection(7)} />
            <Typography variant="subtitle1">
              {contactBaseUrl && <span style={{ color: theme.palette.grey.light }}>{contactBaseUrl}/</span>}
              {userDetails?.contact_details?.split('/').pop()}
            </Typography>
            <Typography {...titleProps} mt={3}>Instruction/Requirement Details</Typography>
            <Typography variant="subtitle1">{userDetails?.instructions}</Typography>
          </Card> */}
        </Box>
      }
      {
        (!isActionsHidden && !selectedEditFormDetails && !isBypassOndato) && <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} mt={2} display={'flex'} justifyContent={'flex-end'}>
          <Button variant="outlined" {...buttonProps} onClick={() => handleOpenReasonDialog('Edit', userDetails?.user, [], EDIT)}>Request Edits</Button>
          <Button
            component="a"
            href={userDetails?.user?.ondato_details?.ondato_url}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            color="info"
            {...buttonProps}
          >
            Ondato details
          </Button>
          <Button variant="contained" color="error" {...buttonProps} onClick={() => handleOpenReasonDialog('Declining', userDetails?.user, DECLINE_CHECKLIST, DECLINE)}>Decline</Button>
          <Button variant="contained" color="success" {...buttonProps} onClick={() => { handleAcceptUser(userDetails?.user); handleClose(); }}>Accept</Button>
        </Stack>
      }
    </GenericDialog >
  );
};